// Social Media Hub Academy — course content, Part A (Sections 1–7)
import type { TrainingSection } from './trainingTypes';

export const SECTIONS_A: TrainingSection[] = [
  // 1 ───────────────────────────────────────────────────────────────────────
  {
    id: 'intro',
    num: 1,
    title: 'Welcome to the Social Media Hub',
    short: 'Introduction',
    icon: 'sparkles',
    minutes: 5,
    intro:
      'The Social Media Hub is the content engine at the heart of the Zera Travel CRM — it learns your brand voice, plans full weeks of luxury content, generates on-brand imagery, routes everything through approval, and hands you publish-ready posts.',
    objectives: [
      'Understand what the Social Media Hub is and the problem it solves',
      'See how the Hub fits into the wider Zera Travel CRM ecosystem',
      'Learn the five-step content engine at a glance',
    ],
    slides: [
      {
        id: 'intro-what',
        title: 'What the Social Media Hub is',
        kicker: 'One engine, five steps, zero blank pages',
        blocks: [
          {
            type: 'lead',
            text: 'Most agencies treat social media as a chore: someone opens a blank caption box every morning and improvises. The Hub replaces that with a system — a repeatable pipeline that turns your brand knowledge into a consistent, luxury-grade Instagram presence.',
          },
          {
            type: 'workflow',
            title: 'The five-step content engine',
            stages: [
              { label: '1 · Brand Voice', desc: 'AI studies your brand and writes a voice profile', tone: 'gold' },
              { label: '2 · Plan Week', desc: 'Generate 7 themed posts from 25 content templates', tone: 'blue' },
              { label: '3 · Review & Media', desc: 'Edit copy, generate AI images, refine prompts', tone: 'neutral' },
              { label: '4 · Approval', desc: 'Draft → review → approve or request revisions', tone: 'green' },
              { label: '5 · Publish', desc: 'Export, schedule, and post to Instagram', tone: 'gold' },
            ],
          },
          {
            type: 'callout',
            tone: 'gold',
            title: 'Why it matters',
            text: 'A recognizable visual language is what separates a premium travel publication from a pile of vacation photos. The Hub bakes that language into every post automatically.',
          },
        ],
        notes:
          'Open with the pain: inconsistent posting, no visual identity, hours lost writing captions. Then reveal the five-step pipeline — pause on each stage for 10 seconds. Close with the promise: a full week of luxury content in minutes, not days. For client demos, this slide is your elevator pitch.',
      },
      {
        id: 'intro-ecosystem',
        title: 'Where the Hub sits in the Zera CRM',
        kicker: 'Connected, not isolated',
        blocks: [
          {
            type: 'hotspots',
            title: 'Tap each part of the ecosystem to explore',
            items: [
              { label: 'Travel Agency CRM', detail: 'Customer forms, journeys and bookings live here. Real client stories become testimonial and highlight posts in the Hub.' },
              { label: 'Social Media Hub', detail: 'The content engine you are learning now — brand voice, weekly generation, media, approval and publishing.' },
              { label: 'Administration Suite', detail: 'Admin approval controls, temporary access codes, and operational security for the whole portal.' },
              { label: 'Content Planner', detail: 'Calendar-style planning surface for scheduling and organizing posts across weeks and campaigns.' },
              { label: 'AI Tools Dashboard', detail: 'Standalone caption writer, bulk caption generator, and auto-scheduling assistant for one-off needs.' },
            ],
          },
          {
            type: 'check',
            question: 'Which step of the content engine comes immediately after generating a week of posts?',
            choices: ['Review & Media (Step 3)', 'Publishing (Step 5)', 'Brand Voice (Step 1)', 'Analytics'],
            correctIndex: 0,
            explain: 'After Step 2 generates the week, Step 3 is where you review copy and generate images before anything moves to approval.',
          },
        ],
        notes:
          'Demonstrate that the Hub is not a bolt-on tool — it reads from the same brand and client data as the rest of the CRM. Click through each hotspot live. The knowledge check keeps the audience engaged early; invite someone to answer aloud in client sessions.',
        takeaways: [
          'The Hub is a five-step pipeline: Voice → Plan → Media → Approval → Publish',
          'It shares data with the CRM, planner and admin suite — one ecosystem',
          'Consistency is the product: every post follows a locked visual language',
        ],
      },
    ],
  },

  // 2 ───────────────────────────────────────────────────────────────────────
  {
    id: 'dashboard',
    num: 2,
    title: 'Dashboard Overview',
    short: 'Dashboard',
    icon: 'layout',
    minutes: 6,
    intro:
      'The admin dashboard is your mission control. Every widget answers one question at a glance: what needs my attention right now?',
    objectives: [
      'Identify every widget on the admin dashboard',
      'Read pipeline status without opening a single post',
      'Jump from any widget to the matching workflow',
    ],
    slides: [
      {
        id: 'dash-widgets',
        title: 'Every widget, explained',
        kicker: 'Mission control',
        blocks: [
          {
            type: 'hotspots',
            title: 'Click each widget to see what it does',
            items: [
              { label: 'Section cards', detail: 'Three launch cards — Travel Agency CRM, Social Media Hub, Administration Suite. Each opens a dedicated workspace.' },
              { label: 'Brand Studio panel', detail: 'The five-step engine embedded in the Social Media Hub view. Shows the current step and week at a glance.' },
              { label: 'Brand Guidelines', detail: 'A live reference card with the Zera palette, typography and tone rules — always visible while you work.' },
              { label: 'Week selector', detail: 'Weeks are named by the dates of their 7 posts (e.g. 6/22/26 – 6/28/26) so you always know exactly which batch you are editing.' },
              { label: 'Status badges', detail: 'Gold = pending, green = approved, red = revision requested. Read the pipeline without opening posts.' },
              { label: 'Content Planner shortcut', detail: 'Top-bar button that jumps straight to the calendar planning surface.' },
            ],
          },
          {
            type: 'callout',
            tone: 'tip',
            title: 'Pro habit',
            text: 'Start every session on the dashboard. Ten seconds of scanning badges tells you whether today is a creation day, a review day, or a publishing day.',
          },
        ],
        notes:
          'Do this slide live inside the real dashboard if possible — flip between this slide and the actual UI. Emphasize the week naming convention (date ranges) because it eliminates the “which week is this?” confusion entirely.',
      },
      {
        id: 'dash-reading',
        title: 'Reading the pipeline at a glance',
        blocks: [
          {
            type: 'steps',
            title: 'The 30-second morning scan',
            items: [
              { title: 'Scan status badges', desc: 'How many posts are pending vs approved? Anything red needs a revision pass.' },
              { title: 'Check the active week', desc: 'Confirm the date-range label matches the week you intend to work on.' },
              { title: 'Look for gaps', desc: 'Days without a post or posts without images are your first tasks.' },
              { title: 'Open the right step', desc: 'Jump directly to Step 3 for media, Step 4 for approvals, Step 5 to export.' },
            ],
          },
          {
            type: 'dodont',
            dos: [
              'Use the date-range week names to communicate with teammates',
              'Clear revision-requested posts before generating new content',
              'Keep one week fully approved before starting the next',
            ],
            donts: [
              'Generate multiple new weeks while older weeks sit unapproved',
              'Ignore red badges — they block the publishing step',
              'Edit posts from two different weeks in the same session without checking the selector',
            ],
          },
        ],
        notes:
          'The do/don’t panel is a coaching moment: most workflow chaos comes from stacking half-finished weeks. Teach “finish the week before starting the next” as a mantra.',
        takeaways: [
          'The dashboard answers “what needs attention?” in under 30 seconds',
          'Weeks are named by their post date range — use those names everywhere',
          'Badges: gold pending, green approved, red revision requested',
        ],
      },
    ],
  },

  // 3 ───────────────────────────────────────────────────────────────────────
  {
    id: 'navigation',
    num: 3,
    title: 'Navigation',
    short: 'Navigation',
    icon: 'compass',
    minutes: 4,
    intro:
      'Everything in the portal is at most two clicks away. This section maps every navigation element so you never hunt for a feature again.',
    objectives: [
      'Locate every navigation item in the admin portal',
      'Understand the two admin realms and how they stay isolated',
      'Use shortcuts to move between workspaces quickly',
    ],
    slides: [
      {
        id: 'nav-map',
        title: 'The complete navigation map',
        blocks: [
          {
            type: 'hotspots',
            title: 'Explore each navigation element',
            items: [
              { label: 'Zera Admin logo', detail: 'Always returns you to the hub home with the three section cards. Your safe “reset” click.' },
              { label: 'Hub button', detail: 'Appears whenever you are inside a section — one tap back to the overview.' },
              { label: 'Content Planner', detail: 'Top-bar shortcut to the calendar planning workspace.' },
              { label: 'Logout', detail: 'Ends the admin session immediately. Sessions are per-browser-tab for security.' },
              { label: 'Section cards', detail: 'CRM, Social Media Hub, and Administration Suite — the three pillars of the portal.' },
              { label: 'Step tabs (Brand Studio)', detail: 'Inside the Hub, the five numbered steps act as tabs. You can revisit any completed step at any time.' },
            ],
          },
          {
            type: 'callout',
            tone: 'info',
            title: 'Two realms, one interface',
            text: 'The Albania portal (/albaniaadmin) and Romania portal (/romaniaadmin) share the same interface but keep completely separate data — brand voice, posts and planner content never mix between realms.',
          },
          {
            type: 'check',
            question: 'You are deep inside Step 3 and want the portal overview. What is the fastest route?',
            choices: ['Click the Zera Admin logo or Hub button', 'Log out and back in', 'Use the browser back button repeatedly', 'Refresh the page'],
            correctIndex: 0,
            explain: 'The logo and Hub button always return you to the overview in one click — no need to lose your session or spam back.',
          },
        ],
        notes:
          'Keep this section brisk — navigation should feel obvious. The realm isolation point is important for multi-brand clients: one login per brand, zero data bleed. That is a strong selling point for agencies managing multiple destinations.',
        takeaways: [
          'Logo / Hub button = one-click return to overview',
          'Five step tabs inside Brand Studio are freely revisitable',
          'Albania and Romania realms are fully isolated data spaces',
        ],
      },
    ],
  },

  // 4 ───────────────────────────────────────────────────────────────────────
  {
    id: 'content-creation',
    num: 4,
    title: 'Content Creation',
    short: 'Content Creation',
    icon: 'pen',
    minutes: 8,
    intro:
      'Content creation in the Hub is template-driven. Twenty-five luxury content templates — from Destination Spotlights to Cinematic Reels — give every post a repeatable visual language.',
    objectives: [
      'Understand the 25-template content system',
      'Generate a full week of posts in one action',
      'Edit any post: caption, hashtags, schedule, format and prompt',
    ],
    slides: [
      {
        id: 'create-templates',
        title: 'Think in templates, not posts',
        kicker: 'The 25-template library',
        blocks: [
          {
            type: 'lead',
            text: 'Every generated post is built from one of 25 content templates. Each template locks together a post theme, a hero image, a pool of supporting shots, and a visual style — so a Food Spotlight always looks like a Food Spotlight.',
          },
          {
            type: 'hotspots',
            title: 'Sample the template library',
            items: [
              { label: 'Destination Spotlight', detail: 'Hero: golden-hour panoramic landscape. Style: luxury travel magazine, warm tones, minimal people.' },
              { label: 'Luxury Hotel Showcase', detail: 'Hero: sunset exterior. Supporting: suites, infinity pool, spa, rooftop bar. Style: architectural photography.' },
              { label: 'Hidden Gems', detail: 'Hero: secret beach or waterfall with no crowds. Style: adventure documentary.' },
              { label: 'Food Spotlight', detail: 'Hero: beautifully plated Albanian cuisine. Supporting: ingredients, chef, wine, dessert.' },
              { label: 'Luxury Experiences', detail: 'Hero: private yacht on turquoise water. Supporting: champagne, helicopter, private driver.' },
              { label: 'Cinematic Travel Reel', detail: 'Drone sunrise opener, slow-motion waves, golden hour — Netflix-documentary color grading.' },
            ],
          },
          {
            type: 'callout',
            tone: 'gold',
            title: 'Mix and match, never repeat',
            text: 'When a week is generated, the engine deals 7 DIFFERENT templates across Monday–Sunday, and randomly chooses the hero shot or a supporting angle for each — real variety inside a consistent identity.',
          },
        ],
        notes:
          'This is the heart of the product philosophy: “don’t think in posts, think in content templates.” For clients, name three or four templates and describe their locked visual style — it makes the feed quality feel inevitable rather than lucky.',
      },
      {
        id: 'create-editing',
        title: 'Editing posts in Step 3',
        kicker: 'Full creative control',
        blocks: [
          {
            type: 'steps',
            title: 'Anatomy of a post card',
            items: [
              { title: 'Header', desc: 'Day, schedule time, status and template badges — plus a mobile-safe delete button with tap-to-confirm.' },
              { title: 'Schedule & Format', desc: 'Change posting day/time and switch between feed post, carousel, reel or story formats.' },
              { title: 'Caption & Copy', desc: 'Edit the caption, hook and hashtags directly — or regenerate with AI while keeping the template.' },
              { title: 'AI Image Prompt', desc: 'The full image prompt is editable. Tweak a detail or copy it for external tools.' },
              { title: 'Media toolbar', desc: 'Five always-visible buttons: Image upload, Video, AI Image, Recreate, Copy Prompt — designed for touch screens.' },
            ],
          },
          {
            type: 'dodont',
            dos: [
              'Keep edits inside the template’s voice — polish, don’t rewrite the identity',
              'Use Recreate when a photo misses, so the copy stays intact',
              'Name and reference weeks by their date range',
            ],
            donts: [
              'Paste captions from outside tools that ignore the brand voice',
              'Delete a whole week to fix one bad post — regenerate that post instead',
            ],
          },
          {
            type: 'check',
            question: 'One post in an otherwise great week has a weak image. Best move?',
            choices: ['Use Recreate on that single post', 'Delete the week and regenerate', 'Manually write a new prompt from scratch', 'Publish it anyway'],
            correctIndex: 0,
            explain: 'Recreate re-rolls the image shot within the same template while preserving the approved copy — surgical, not destructive.',
          },
        ],
        notes:
          'Demo flow: open a post card, change the time, tweak two words in the caption, hit Recreate on the image, then Copy Prompt. Five interactions in under a minute — that is the speed story you want the client to feel.',
        takeaways: [
          '25 templates × hero/supporting shots = variety with identity',
          'Every field of a post is editable in Step 3',
          'Recreate fixes images without touching approved copy',
        ],
      },
    ],
  },

  // 5 ───────────────────────────────────────────────────────────────────────
  {
    id: 'ai-writing',
    num: 5,
    title: 'AI Writing Tools',
    short: 'AI Writing',
    icon: 'wand',
    minutes: 7,
    intro:
      'The Hub’s writing AI is trained on YOUR brand voice — not a generic internet tone. This section covers voice analysis, caption generation, and the standalone AI writing tools.',
    objectives: [
      'Run a brand voice analysis and understand its output',
      'Generate and regenerate captions that stay on-voice',
      'Use the Caption Tool and Bulk Caption Tool for ad-hoc needs',
    ],
    slides: [
      {
        id: 'ai-voice',
        title: 'Brand voice: the foundation of every caption',
        kicker: 'Step 1 of the engine',
        blocks: [
          {
            type: 'lead',
            text: 'Before writing a single caption, the engine analyzes your brand — tone, vocabulary, audience, and positioning — and produces a voice profile. Every caption generated afterwards is written through that profile.',
          },
          {
            type: 'steps',
            title: 'How voice-driven writing works',
            items: [
              { title: 'Analyze', desc: 'Step 1 studies your brand inputs and writes a structured voice profile (tone, phrases, audience, no-go words).' },
              { title: 'Generate', desc: 'Step 2 writes 7 captions through the voice profile AND the assigned content template — a hotel showcase reads like a hotel showcase.' },
              { title: 'Refine', desc: 'Regenerate any single post; the engine recovers its template and rewrites within it, keeping the week coherent.' },
            ],
          },
          {
            type: 'callout',
            tone: 'warn',
            title: 'Common mistake',
            text: 'Re-running voice analysis with throwaway inputs degrades every future caption. Treat the voice profile like a brand asset: update it deliberately, not casually.',
          },
        ],
        notes:
          'Explain the difference between “AI that writes” and “AI that writes as YOU.” Show a caption and point out voice markers: the vocabulary, the rhythm, the sign-off style. Clients consistently underestimate this until they see a side-by-side with generic AI copy.',
      },
      {
        id: 'ai-tools',
        title: 'The standalone writing tools',
        blocks: [
          {
            type: 'hotspots',
            title: 'Three tools for ad-hoc writing',
            items: [
              { label: 'Caption Tool', detail: 'Describe a photo or moment and get an on-voice caption with hook + hashtags. Perfect for spontaneous posts outside the weekly plan.' },
              { label: 'Bulk Caption Tool', detail: 'Paste a list of topics and generate a batch of captions in one run — ideal for catching up or seeding a campaign.' },
              { label: 'Caption Suggest (in-editor)', detail: 'Inside Step 3, request alternate takes on any caption without leaving the post card.' },
            ],
          },
          {
            type: 'dodont',
            dos: ['Give the AI concrete details: place, season, feeling, audience', 'Pick the best of several generations — taste is your job', 'Keep hooks under 8 words for mobile feeds'],
            donts: ['Accept the first output when it feels 80% right — regenerate', 'Stack more than 8–10 hashtags on a luxury feed'],
          },
          {
            type: 'check',
            question: 'What makes Hub captions different from generic AI captions?',
            choices: ['They are written through your analyzed brand voice profile', 'They are longer', 'They use more emojis', 'They are written by human editors'],
            correctIndex: 0,
            explain: 'The voice profile from Step 1 shapes tone, vocabulary and rhythm of every caption — that is the differentiator.',
          },
        ],
        notes:
          'Live demo idea: run the Caption Tool with “sunset dinner in Gjirokastër, honeymoon couple, September” and read the result aloud. Then show the Bulk tool with 5 topics. Total demo time: 2 minutes, high wow-factor.',
        takeaways: [
          'Voice profile first, captions second — always',
          'Caption Tool for one-offs, Bulk Tool for batches, Suggest for alternates',
          'Concrete inputs produce premium outputs',
        ],
      },
    ],
  },

  // 6 ───────────────────────────────────────────────────────────────────────
  {
    id: 'ai-images',
    num: 6,
    title: 'AI Image Generation',
    short: 'AI Images',
    icon: 'image',
    minutes: 7,
    intro:
      'Every post can carry a template-styled AI image: the prompt is assembled from the chosen shot, the template’s locked visual style, and the brand palette.',
    objectives: [
      'Generate an on-brand image for any post',
      'Understand how prompts are assembled (shot + style + palette)',
      'Recreate, edit prompts, and upload your own media',
    ],
    slides: [
      {
        id: 'img-engine',
        title: 'How the image engine thinks',
        blocks: [
          {
            type: 'workflow',
            title: 'Prompt assembly pipeline',
            stages: [
              { label: 'Shot', desc: 'Hero (~55%) or a random supporting angle from the template', tone: 'blue' },
              { label: 'Visual style', desc: 'The template’s locked look — e.g. “architectural photography, soft warm lighting”', tone: 'gold' },
              { label: 'Brand palette', desc: 'Zera gold, deep navy, warm neutrals woven into the scene', tone: 'neutral' },
              { label: 'Render', desc: 'The AI generates the final image and attaches it to the post', tone: 'green' },
            ],
          },
          {
            type: 'callout',
            tone: 'gold',
            title: 'Template-locked style',
            text: 'A Hidden Gems post renders like an adventure documentary; a Hotel Showcase renders like an architecture magazine. The style never randomly drifts — that is what builds a recognizable feed.',
          },
        ],
        notes:
          'Draw the contrast: most AI feeds look chaotic because every image has a random style. Here, style is locked per template. If demoing live, generate one Hotel Showcase image and one Hidden Gems image back-to-back and let the stylistic difference speak.',
      },
      {
        id: 'img-controls',
        title: 'Your image controls in Step 3',
        blocks: [
          {
            type: 'hotspots',
            title: 'The five-button media toolbar',
            items: [
              { label: 'Image', detail: 'Upload your own photo — real photography always wins when you have it.' },
              { label: 'Video', detail: 'Attach video for reels and stories formats.' },
              { label: 'AI Image', detail: 'Generate a fresh render from the current prompt.' },
              { label: 'Recreate', detail: 'Re-roll the shot (hero vs supporting) within the template for a genuinely different photo.' },
              { label: 'Copy Prompt', detail: 'Copy the full assembled prompt to use in any external image tool.' },
            ],
          },
          {
            type: 'dodont',
            dos: ['Edit the prompt to add specifics (season, time of day, angle)', 'Prefer real photos for testimonials and team posts', 'Recreate until the image earns the feed'],
            donts: ['Strip out the visual-style portion of a prompt — that breaks the template look', 'Use low-resolution uploads on a luxury feed'],
          },
          {
            type: 'check',
            question: 'What is the difference between “AI Image” and “Recreate”?',
            choices: [
              'AI Image renders the current prompt; Recreate re-rolls the shot within the template first',
              'They are identical',
              'Recreate only works on videos',
              'AI Image deletes the old image permanently',
            ],
            correctIndex: 0,
            explain: 'Recreate changes WHICH shot is rendered (hero vs supporting angle) before rendering; AI Image simply renders the prompt as-is.',
          },
        ],
        notes:
          'The toolbar is always visible — including on phones — so field edits are practical. Mention the mobile story: an admin can approve and fix a week from a beach chair. That line lands well in demos.',
        takeaways: [
          'Prompts = shot + template style + brand palette',
          'Recreate re-rolls the shot; AI Image renders the current prompt',
          'Real photography beats AI when you have it — upload freely',
        ],
      },
    ],
  },

  // 7 ───────────────────────────────────────────────────────────────────────
  {
    id: 'library',
    num: 7,
    title: 'Content Library',
    short: 'Content Library',
    icon: 'folder',
    minutes: 5,
    intro:
      'Everything you create is stored, named and retrievable: weeks named by date range, posts with full history, and reusable media in shared storage buckets.',
    objectives: [
      'Navigate weeks, posts and stored media',
      'Use date-range week names to stay organized',
      'Maintain a reusable B-roll library',
    ],
    slides: [
      {
        id: 'lib-organization',
        title: 'How content is organized',
        blocks: [
          {
            type: 'bullets',
            title: 'The three layers of the library',
            items: [
              'Weeks — batches of 7 posts, named by their date range (e.g. 6/22/26 – 6/28/26)',
              'Posts — each carries its template, caption, prompt, media, status and schedule',
              'Media — uploaded and generated assets stored in the content-planner and brand-assets buckets',
            ],
          },
          {
            type: 'callout',
            tone: 'tip',
            title: 'The universal B-roll mindset',
            text: 'Maintain shared folders by category — aerial drone, beaches, luxury hotels, food, festivals, sunsets. A rich B-roll library lets any post type pull cohesive visuals instantly.',
          },
          {
            type: 'dodont',
            dos: ['Delete only with intent — week deletion removes its posts too', 'Reuse strong media across templates', 'Audit the library monthly and clear duds'],
            donts: ['Hoard every failed AI render', 'Keep test weeks lying around — they clutter the selectors'],
          },
          {
            type: 'check',
            question: 'How are generated weeks named in the menus?',
            choices: ['By the date range of their 7 posts', 'Week 1, Week 2, Week 3…', 'By the admin who created them', 'Randomly generated codenames'],
            correctIndex: 0,
            explain: 'Weeks are labeled by their post date range — e.g. 6/22/26 – 6/28/26 — so every menu tells you exactly which batch you are looking at.',
          },
        ],
        notes:
          'Keep this section practical. Show the week selector with two or three date-range labels, open an old week, and show that nothing is ever lost. The monthly audit habit is worth repeating twice.',
        takeaways: [
          'Weeks → posts → media: three clean layers',
          'Date-range names make every week self-describing',
          'A curated B-roll library is a compounding asset',
        ],
      },
    ],
  },
];
